'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

const clarityFormSchema = z.object({
  projectId: z.string()
    .min(1, 'Project ID is required')
    .regex(/^[a-zA-Z0-9]{1,}$/, 'Invalid Microsoft Clarity Project ID format'),
  events: z.object({
    PageView: z.boolean(),
    InitiateCheckout: z.boolean(),
    Purchase: z.boolean(),
    AddToCart: z.boolean(),
    ViewContent: z.boolean(),
  }),
})

type FormValues = z.infer<typeof clarityFormSchema>

interface ClarityFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function ClarityForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: ClarityFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(clarityFormSchema),
    defaultValues: {
      projectId: defaultValues.settings?.projectId ?? '',
      events: {
        PageView: defaultValues.settings?.events?.PageView ?? true,
        InitiateCheckout: defaultValues.settings?.events?.InitiateCheckout ?? true,
        Purchase: defaultValues.settings?.events?.Purchase ?? true,
        AddToCart: defaultValues.settings?.events?.AddToCart ?? true,
        ViewContent: defaultValues.settings?.events?.ViewContent ?? true,
      },
    },
  })

  const { register, handleSubmit, formState: { errors }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { events, ...settings } = data
    await onSubmit({
      settings: {
        ...settings,
        events,
      },
    })
  }

  const EVENTS = {
    PageView: 'Page View',
    InitiateCheckout: 'Initiate Checkout',
    Purchase: 'Purchase',
    AddToCart: 'Add to Cart',
    ViewContent: 'View Content',
  } as const

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Info Alert */}
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>{t('clarity.aboutTitle')}</AlertTitle>
        <AlertDescription>
          {t('clarity.aboutDescription')}
        </AlertDescription>
      </Alert>

      {/* Project Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>{t('clarity.projectConfiguration')}</CardTitle>
          <CardDescription>{t('clarity.projectConfigDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="projectId">
              {t('clarity.projectId')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="projectId"
              placeholder={t('clarity.projectIdPlaceholder')}
              disabled={isLoading}
              {...register('projectId')}
            />
            <p className="text-xs text-muted-foreground">
              {t('clarity.projectIdHint')}
            </p>
            {errors.projectId && (
              <p className="text-xs text-destructive">{errors.projectId.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Event Tracking */}
      <Card>
        <CardHeader>
          <CardTitle>{t('clarity.eventTracking')}</CardTitle>
          <CardDescription>{t('clarity.eventTrackingDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(EVENTS).map(([key, label]) => (
              <div key={key} className="flex items-center space-x-2">
              <Checkbox
                id={`event-${key}`}
                checked={watch(`events.${key as keyof typeof EVENTS}`)}
                onCheckedChange={(checked) => setValue(`events.${key as keyof typeof EVENTS}`, checked as boolean)}
                disabled={isLoading}
              />
                <Label htmlFor={`event-${key}`} className="cursor-pointer font-normal">
                  {label}
                </Label>
              </div>
            ))}
            </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('clarity.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
            <li>
                {t('clarity.setupStep1')}{' '}
                <a
                  href="https://clarity.microsoft.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {t('clarity.setupStep1Link')}
                </a>
            </li>
              <li dangerouslySetInnerHTML={{ __html: t('clarity.setupStep2') }} />
              <li dangerouslySetInnerHTML={{ __html: t('clarity.setupStep3') }} />
              <li>{t('clarity.setupStep4')}</li>
          </ol>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
